// Shared formatting/math helpers for the People Card "N TIME ZONES" detail group.
// Spec: Notion "Multi-TZ Sync Up Detail Group" (e8f3d605c41448f98d39588e55f18c8e), parent
// "People Card" (d45923fee2264c559a5512601920958c), tree Mobile — Design Spec → TeeUp.
//
// NOTE: this is intentionally SEPARATE from the legacy `Content/Timezone/TimezoneItem`, which
// is still shared with the Suggest-When flow (SuggestWhenModalNd/TimeWheelTab/TimeWheel/GroupInfo)
// and left untouched by this change (out of scope, do not regress the When flow).

import { getCountry, getTimezone } from "countries-and-timezones"
import { countryCodeEmoji } from "country-code-emoji"

import dayjs from "@utils/dayjs"

// The spec's own example ("USA (CDT +1)") uses short country names; fall back to the full
// country name from countries-and-timezones for anything not covered here.
const COUNTRY_SHORT_NAMES = {
  US: "USA",
  GB: "UK",
}

export const getZoneOffsetMinutes = (zone, at = dayjs()) => {
  try {
    return at.tz(zone).utcOffset()
  } catch (error) {
    console.error("Failed to resolve timezone offset", { zone, error })
    return 0
  }
}

export const getZoneAbbreviation = (zone, at = dayjs()) => {
  try {
    return at.tz(zone).offsetName("short") || zone
  } catch (error) {
    console.error("Failed to resolve timezone abbreviation", { zone, error })
    return zone
  }
}

export const getZoneCountry = zone => {
  try {
    const code = getTimezone(zone)?.countries?.[0]
    if (!code) return { code: "", name: "", flag: "" }

    const name = COUNTRY_SHORT_NAMES[code] || getCountry(code)?.name || code
    return { code, name, flag: countryCodeEmoji(code) }
  } catch (error) {
    console.error("Failed to resolve timezone country", { zone, error })
    return { code: "", name: "", flag: "" }
  }
}

// 12h vs 24h per device/locale convention (spec: "12/24h per locale/device convention").
const HOUR12 = (() => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
    }).resolvedOptions().hour12
  } catch {
    return true
  }
})()

export const formatZoneTime = zoneTime =>
  HOUR12 ? zoneTime.format("h:mm A") : zoneTime.format("HH:mm")

// Abbreviated day/month per locale (spec: "current date below (abbrev day/month per locale)").
export const formatZoneDate = zoneTime => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(zoneTime.toDate())
  } catch {
    return zoneTime.format("ddd MMM D")
  }
}

export const getDisplayName = participant =>
  participant?.name ||
  participant?.username ||
  participant?.matchingContact?.split("@")[0] ||
  "Guest"

// Spec: offset-line dot position formula. indicatorLineLength (L) and dot radius (r) are in the
// same px units as the rendered indicator.
//   segmentSize = (L - 2r) / 96
//   midPoint = L / 2
//   0 < offset <= 12: pos = mid + offset*3*segmentSize
//   offset > 12: pos = mid + offset*segmentSize + 24*segmentSize
//   -12 <= offset < 0: pos = mid + offset*3*segmentSize (mirrored)
//   offset < -12: pos = mid + offset*segmentSize - 24*segmentSize
export const getOffsetDotPosition = (offsetHours, lineLength, dotRadius) => {
  const segmentSize = (lineLength - 2 * dotRadius) / 96
  const midPoint = lineLength / 2

  if (!offsetHours) return midPoint
  if (offsetHours > 0 && offsetHours <= 12) {
    return midPoint + offsetHours * 3 * segmentSize
  }
  if (offsetHours > 12) {
    return midPoint + offsetHours * segmentSize + 24 * segmentSize
  }
  if (offsetHours < 0 && offsetHours >= -12) {
    return midPoint + offsetHours * 3 * segmentSize
  }
  return midPoint + offsetHours * segmentSize - 24 * segmentSize
}
